<template>
    <BaseForm>
        <template #fields>
            <MultiLinkGroup>
                <MultiLink
                    :label="$t('external-data')"
                    :links="values.externalData"
                    :error-messages="errors.externalData"
                    @input="setFieldValue('externalData', $event)"
                    @delete="setFieldValue('externalData', $event)"
                />
                <MultiLink
                    :label="$t('internal-data')"
                    :links="values.internalData"
                    :error-messages="errors.internalData"
                    @input="setFieldValue('internalData', $event)"
                    @delete="setFieldValue('internalData', $event)"
                />
            </MultiLinkGroup>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import MultiLinkGroup from '@/components/MultiLink/MultiLinkGroup.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesDataSchema } from '../schemas';

defineProps<{ seriesId: string }>();

const { series, createEventSeriesMultiLink } = useEventSeriesInjection();
const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createEventSeriesMultiLink);

const externalDataId = computed(() => series.value?.externalData?.id);
const internalDataId = computed(() => series.value?.internalData?.id);

const externalDataLinks = computed(() => series.value?.externalData?.links.nodes ?? []);
const internalDataLinks = computed(() => series.value?.internalData?.links.nodes ?? []);

const { errors, values, setFieldValue, validate, onDiscard, resetForm } = useForm(
    EventSeriesDataSchema,
    computed(() => ({
        externalData: getFormFieldLinks(externalDataLinks),
        internalData: getFormFieldLinks(internalDataLinks),
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await saveMultiLinkFields([
            { id: externalDataId.value, fieldName: 'externalData', links: values.value.externalData },
            { id: internalDataId.value, fieldName: 'internalData', links: values.value.internalData },
        ]);
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
